
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Section {
  id: number;
  order: number;
  prompt: string;
  date: string | null;
  description: string;
  title: string;
  includeInReport: boolean;
  visible: boolean;
  canClone: boolean;
  key: string;
  temperature: number;
  preset: string | null;
  model: string;
  canDelete: boolean;
  checked: boolean;
  editable: boolean;
}

interface DeleteSectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: Section | null;
  onConfirm: () => void;
}

export const DeleteSectionDialog = ({ open, onOpenChange, section, onConfirm }: DeleteSectionDialogProps) => {
  const { t } = useTranslation();

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Content
        className={cn(
          "fixed left-[50%] top-[50%] z-[20000] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
        )}
      >
        <div className="flex flex-col space-y-2 text-center sm:text-left">
          <AlertDialogPrimitive.Title className="text-lg font-semibold">
            {t('confirm_delete_section')}
          </AlertDialogPrimitive.Title>
          <AlertDialogPrimitive.Description className="text-sm text-muted-foreground">
            {t('action_cannot_undone')}
            {section && (
              <div className="mt-2 p-2 bg-muted rounded text-sm">
                <strong>{section.title}</strong>
                <br />
                {section.description}
              </div>
            )}
          </AlertDialogPrimitive.Description>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
          <AlertDialogPrimitive.Cancel
            className={cn(
              buttonVariants({ variant: "outline" }),
              "mt-2 sm:mt-0"
            )}
          >
            {t('cancel')}
          </AlertDialogPrimitive.Cancel>
          <AlertDialogPrimitive.Action
            onClick={handleConfirm}
            className={cn(buttonVariants(), "bg-destructive text-destructive-foreground hover:bg-destructive/90")}
          >
            {t('delete')}
          </AlertDialogPrimitive.Action>
        </div>
      </AlertDialogPrimitive.Content>
    </AlertDialogPrimitive.Root>
  );
};
